import type {
	Attribution,
	Confidence,
	DetectionLayer,
	SessionAbortReason,
} from './normalized-telemetry-event';

/**
 * Result of routing a (symptom, detectionLayer) pair to an attribution domain.
 *
 * Returned from {@link attributeError} and intended to be spread directly into
 * the `attribution` / `confidence` fields of a normalized telemetry event.
 */
export type AttributionResult = {
	attribution: Attribution;
	confidence: Confidence;
};

/**
 * Routes a (symptom, detectionLayer) pair to an attribution domain.
 *
 * **Detection point ≠ ownership.** For example, the frontend parser detecting
 * a malformed final ADF should be attributed to `backend_generation_quality`
 * (the model produced bad output), not to `frontend_parser_robustness` (the
 * parser merely surfaced it). This routing table encodes that mapping in one
 * place so every event in the pipeline produces consistent attribution
 * without re-implementing the rules.
 *
 * Unrecognised pairs return `unknown`/`low` so callers always get a usable
 * answer and dashboards can filter on confidence to focus on high-signal
 * events.
 *
 * `abortReason` is an optional discriminator for abort symptoms, which are
 * otherwise ambiguous: `session_aborted` covers both "we don't know why" and
 * "an error on the apply path forced it". Routing stays centralised in this
 * one table rather than being overridden at call sites.
 *
 * See the design doc — "Attribution Guidance" — for rationale.
 */
export function attributeError(
	symptom: string,
	detectionLayer: DetectionLayer,
	abortReason?: SessionAbortReason,
): AttributionResult {
	// ─── Parser-detected symptoms ────────────────────────────────────────
	// The streaming-JSON parser (in ai-model-io / ADFStreamer) is the
	// detector here, but the *cause* in every case is upstream — the model
	// produced output that the parser had to reject or repair. Detection
	// point ≠ ownership: we attribute to the backend.

	// Parser detecting malformed final ADF → backend generated bad output.
	// High confidence because the parser does not modify content — if the
	// final ADF is invalid, it's invalid as emitted by the model.
	if (detectionLayer === 'parser' && symptom === 'final_adf_invalid') {
		return { attribution: 'backend_generation_quality', confidence: 'high' };
	}
	// Parser had to repair JSON → backend streamed invalid JSON. Medium
	// confidence because some JSON-repair recoveries are triggered by
	// transport-level glitches (truncation, re-ordering) rather than true
	// model-output errors.
	if (detectionLayer === 'parser' && symptom === 'final_json_repaired') {
		return { attribution: 'backend_generation_quality', confidence: 'medium' };
	}

	// ─── Orchestrator-detected symptoms ──────────────────────────────────
	// The orchestrator is responsible for translating streamed ADF into
	// ProseMirror transactions and managing session lifecycle. Symptoms it
	// detects can split between frontend bugs (apply logic, lifecycle) and
	// backend bugs (the model emitted ADF that doesn't conform to the
	// schema or references a node that doesn't exist).

	// Orchestrator session lifecycle error → frontend issue. The session
	// machine itself is FE-owned; lifecycle errors are almost always our
	// bugs.
	if (detectionLayer === 'orchestrator' && symptom === 'session_lifecycle_error') {
		return { attribution: 'frontend_apply_logic', confidence: 'high' };
	}
	// Apply step failed with ProseMirror transaction error → FE apply
	// logic. The transaction was constructed by our code; if PM rejects
	// it, our construction is wrong.
	if (detectionLayer === 'orchestrator' && symptom === 'apply_failed_pm_error') {
		return { attribution: 'frontend_apply_logic', confidence: 'high' };
	}
	// Apply step failed because incoming ADF node had schema errors →
	// backend. The orchestrator only attempts to apply what the model
	// emitted; if the schema check fails, the model is at fault.
	if (detectionLayer === 'orchestrator' && symptom === 'apply_failed_adf_schema_error') {
		return { attribution: 'backend_generation_quality', confidence: 'high' };
	}
	// Valid requested replacement structure was lost by the frontend apply pipeline.
	if (detectionLayer === 'orchestrator' && symptom === 'applied_structure_mismatch') {
		return { attribution: 'frontend_apply_logic', confidence: 'high' };
	}
	// Target node not found → LLM hallucinated a localId. The orchestrator
	// looks up the target by the model-supplied localId; if no such node
	// exists in the doc, the model invented an identifier.
	if (detectionLayer === 'orchestrator' && symptom === 'local_id_not_found') {
		return { attribution: 'backend_generation_quality', confidence: 'high' };
	}
	// Node disappeared that wasn't the explicit target → frontend apply
	// side effect. Medium confidence because a poorly-formed transaction
	// can collateral-damage a sibling, but it could also be backend asking
	// for a structural change that we translated correctly into a
	// destructive transaction.
	if (detectionLayer === 'orchestrator' && symptom === 'unexpected_node_removal') {
		return { attribution: 'frontend_apply_logic', confidence: 'medium' };
	}
	// Tool call applied successfully but produced no document change.
	// Highest-likelihood cause is the model emitting content identical to
	// what already exists in the document (redundant generation). Medium
	// confidence rather than high because frontend apply-layer normalisation
	// can also silently collapse a transaction to a no-op (e.g. ProseMirror
	// merging adjacent identical text runs). A future refinement comparing
	// pre/post node-content hashes could upgrade this to high confidence
	// when the replacement content is byte-identical to the existing content.
	if (detectionLayer === 'orchestrator' && symptom === 'no_document_change') {
		return { attribution: 'backend_generation_quality', confidence: 'medium' };
	}
	// Abort forced by an error thrown on the streaming/apply path. The
	// abort itself is our reaction to a failure in frontend-owned code, so
	// unlike an undiscriminated abort this one has a known owner. Checked
	// before the generic `session_aborted` entry below so the discriminated
	// case wins.
	if (
		detectionLayer === 'orchestrator' &&
		symptom === 'session_aborted' &&
		abortReason === 'aborted_due_to_error'
	) {
		return { attribution: 'frontend_apply_logic', confidence: 'high' };
	}
	// User explicitly cancelled the session. Not a failure of any
	// component, so there is nothing to attribute — but we are highly
	// confident that "nobody is at fault" is the right answer, hence
	// unknown/high rather than unknown/low.
	if (detectionLayer === 'orchestrator' && symptom === 'user_aborted') {
		return { attribution: 'unknown', confidence: 'high' };
	}
	// User / UI explicitly aborted the session. Origin is opaque to
	// telemetry (could be intentional cancel, navigation away, etc.) so we
	// can't confidently attribute the cause — only the fact of the abort
	// itself.
	if (detectionLayer === 'orchestrator' && symptom === 'session_aborted') {
		return { attribution: 'unknown', confidence: 'high' };
	}
	// Stream stalled: no further commands arrived within the orchestrator's
	// queue timeout. The most common cause is the backend halting its
	// stream (network slowness, model latency, or upstream error) so we
	// attribute to backend generation quality with medium confidence —
	// frontend bugs can also stall the stream, so this is not high
	// confidence.
	if (detectionLayer === 'orchestrator' && symptom === 'session_timeout') {
		return { attribution: 'backend_generation_quality', confidence: 'medium' };
	}
	// Post-apply analyzer detected a top-level node removed that no tool
	// call had explicitly targeted. When the analyzer used the precise
	// backend-supplied selection set we can confidently attribute to the
	// model. The proxy-fallback path (no backend selection) is handled at
	// the call site, where it returns unknown/low instead.
	if (detectionLayer === 'post_apply' && symptom === 'unexpected_content_removal') {
		return { attribution: 'backend_generation_quality', confidence: 'high' };
	}
	// Post-apply analyzer detected text content of a top-level node changed
	// that no tool call had explicitly targeted. Heuristic-driven (uses
	// character-count inequality with a `±1` ProseMirror normalisation
	// tolerance) so low-confidence — the caller is expected to also flag
	// the event with `experimental: true`. Same-length rewrites are silent
	// false negatives by design (out of scope for v1).
	if (detectionLayer === 'post_apply' && symptom === 'unintended_text_modification') {
		return { attribution: 'unknown', confidence: 'low' };
	}
	// Mutating tool calls ran but the document is effectively unchanged.
	// Highest-likelihood cause is a model that emitted commands targeting
	// the wrong node or producing no-op transactions; medium confidence
	// because frontend bugs (e.g. apply-layer no-op merges) can also
	// produce this signal.
	if (detectionLayer === 'post_apply' && symptom === 'no_meaningful_effect') {
		return { attribution: 'backend_generation_quality', confidence: 'medium' };
	}
	// Inferred intent doesn't match structural delta (e.g. APPEND intent
	// but net node removal). Heuristic-driven and so low-confidence — the
	// caller is expected to also flag the event with `experimental: true`.
	if (detectionLayer === 'post_apply' && symptom === 'semantic_mismatch') {
		return { attribution: 'unknown', confidence: 'low' };
	}

	// Default: unknown.
	return { attribution: 'unknown', confidence: 'low' };
}
